import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { AlertCircle, CheckCircle2, CreditCard } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Resubscribe({ user }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [subscription, setSubscription] = useState(null);

  useEffect(() => {
    fetchSubscriptionStatus();
  }, []);

  const fetchSubscriptionStatus = async () => {
    try {
      const response = await axios.get(`${API}/subscription/status`);
      setSubscription(response.data);
    } catch (error) {
      console.error('Failed to fetch subscription:', error);
    }
  };

  const handleResubscribe = async () => {
    setLoading(true);
    try {
      // Use the self-serve upgrade endpoint — works for both trial-expired and cancelled-sub flows
      const response = await axios.post(`${API}/subscription/checkout`);
      window.location.href = response.data.checkout_url;
    } catch (error) {
      console.error('Resubscribe error:', error);
      alert(error.response?.data?.detail || 'Failed to start checkout. Please try again or contact support.');
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
      <Card className="max-w-2xl w-full shadow-xl">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center">
            <AlertCircle className="h-8 w-8 text-orange-600" />
          </div>
          <CardTitle className="text-3xl font-bold text-gray-900">
            Subscription Expired
          </CardTitle>
          <CardDescription className="text-lg mt-2">
            Your subscription ended on {subscription?.ends_at ? new Date(subscription.ends_at).toLocaleDateString() : 'recently'}
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-6">
          {/* What you're missing */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
            <h3 className="font-semibold text-lg mb-4 text-blue-900">
              Renew to continue enjoying:
            </h3>
            <ul className="space-y-3">
              <li className="flex items-start gap-3">
                <CheckCircle2 className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
                <span className="text-gray-700">Personalized meal plans updated weekly</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle2 className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
                <span className="text-gray-700">Your weight loss journey under the supervision of Coach Knut</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle2 className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
                <span className="text-gray-700">Progress tracking and weekly check-ins</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle2 className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
                <span className="text-gray-700">Access to your meal history and recipes</span>
              </li>
            </ul>
          </div>

          {/* Saved data notice */}
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <p className="text-sm text-green-800">
              <strong>Good news!</strong> All your meal plans, progress data, and history are safely saved. 
              Renew now to pick up right where you left off.
            </p>
          </div>

          {/* CTA Buttons */}
          <div className="space-y-3 pt-4">
            <Button
              data-testid="resubscribe-upgrade-btn"
              onClick={handleResubscribe}
              disabled={loading}
              className="w-full h-14 text-lg bg-[#1a61e8] hover:bg-[#1553c9]"
            >
              <CreditCard className="mr-2 h-5 w-5" />
              {loading ? 'Redirecting...' : 'Continue with £27/month'}
            </Button>

            <p className="text-center text-sm text-gray-500">
              Cancel anytime. Your data stays safe.
            </p>

            <Button
              variant="outline"
              onClick={() => navigate('/')}
              className="w-full"
            >
              Back to Home
            </Button>
          </div>

          {/* Support */}
          <p className="text-center text-sm text-gray-500 pt-4">
            Questions? Contact Coach Knut at{' '}
            <a href="mailto:getmealguide@gmail.com" className="text-blue-600 hover:underline">
              getmealguide@gmail.com
            </a>
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
